/**
 * The station's local credentials — the single reader of both.
 *
 * The device token is a long-lived credential parked on a shared lobby
 * machine, which is a deliberate trade and not an oversight. It is what
 * `gate_device` was built for: hashed at rest server-side (`secretHash`),
 * bound to one gate, revocable from `/reglages/appareils`, and
 * `lastSeenAt`-tracked. The alternative — a plain gardien web login — would
 * leave a 7-day member session cookie in the same browser with none of those
 * properties.
 *
 * `localStorage` throws outright in some privacy modes rather than returning
 * null, so every access is wrapped. A station whose storage is unavailable
 * degrades to "unpaired" on each load, which is the honest reading: it cannot
 * hold a credential, so it does not have one.
 */

const TOKEN_KEY = "gately_gate_device_token";
/**
 * `mustChangePin` is only ever reported by the `start-shift` response — no
 * query exposes `member.mustChangePin` — so the flag is parked here to survive
 * the page reloads a browser kiosk gets and a phone never does. It is a nudge,
 * not a control: the server does not enforce it, so a stale `true` costs one
 * extra prompt and a stale `false` costs nothing the mobile app doesn't
 * already cost.
 */
const MUST_CHANGE_PIN_KEY = "gately_gate_device_must_change_pin";

function read(key: string): string | null {
  try {
    return globalThis.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    globalThis.localStorage.setItem(key, value);
  } catch {
    // Nothing to do — see the module docblock.
  }
}

function remove(key: string): void {
  try {
    globalThis.localStorage.removeItem(key);
  } catch {
    // Nothing to do — see the module docblock.
  }
}

/** Sync so the oRPC header builder can use it without awaiting. */
export function getDeviceToken(): string | null {
  return read(TOKEN_KEY);
}

export function setDeviceToken(token: string): void {
  write(TOKEN_KEY, token);
}

/**
 * Unpairs this browser. Deliberately clears the PIN flag too: it belongs to a
 * gardien session that this token was the only way to open.
 */
export function clearDeviceToken(): void {
  remove(TOKEN_KEY);
  remove(MUST_CHANGE_PIN_KEY);
}

export function getMustChangePin(): boolean {
  return read(MUST_CHANGE_PIN_KEY) === "1";
}

export function setMustChangePin(value: boolean): void {
  if (value) write(MUST_CHANGE_PIN_KEY, "1");
  else remove(MUST_CHANGE_PIN_KEY);
}
